import { apiRequest, invalidateCache, API_BASE } from '../../shared/api/base'
import { supabase } from '../../shared/lib/supabase'
import { convertToJpeg } from '../../shared/utils/image'
import type { NotebookNote } from '../../shared/types/notebook'

// ─── Response types ─────────────────────────────────────────

export interface NotebookListResponse {
  entries: NotebookNote[]
}

export interface NotebookEntryResponse {
  entry: NotebookNote
  children: NotebookNote[]
}

// ─── API functions ──────────────────────────────────────────

export async function getNotebookEntries(parentId?: string): Promise<NotebookListResponse> {
  const params = parentId ? `?parentId=${parentId}` : ''
  return apiRequest<NotebookListResponse>(`/notebook${params}`)
}

export async function getNotebookEntry(id: string): Promise<NotebookEntryResponse> {
  return apiRequest<NotebookEntryResponse>(`/notebook/${id}`)
}

export async function createNotebookEntry(data: {
  title: string
  contentMd: string
  parentId?: string | null
  icon?: string
  tags?: string[]
  pinned?: boolean
  scope?: string
}): Promise<NotebookNote> {
  // Invalidate BEFORE the request — if user switches tabs while the request
  // is in-flight, loadData() must hit the server, not a stale cache entry.
  invalidateCache('/notebook')
  return apiRequest<NotebookNote>('/notebook', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateNotebookEntry(
  id: string,
  data: Partial<Pick<NotebookNote, 'title' | 'contentMd' | 'tags' | 'icon' | 'pinned' | 'sortOrder' | 'parentId' | 'summary' | 'scope' | 'userNotesMd'>> & { updatedAt?: string },
): Promise<NotebookNote> {
  invalidateCache('/notebook')
  invalidateCache(`/notebook/${id}`)
  return apiRequest<NotebookNote>(`/notebook/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteNotebookEntry(id: string): Promise<{ id: string; deleted: boolean }> {
  invalidateCache('/notebook')
  invalidateCache(`/notebook/${id}`)
  return apiRequest<{ id: string; deleted: boolean }>(`/notebook/${id}`, {
    method: 'DELETE',
  })
}

/** Ask the backend to pick an emoji icon for a title via Haiku */
export async function pickNoteIcon(title: string, contentMd?: string): Promise<string> {
  const res = await apiRequest<{ icon: string }>('/notebook/pick-icon', {
    method: 'POST',
    body: JSON.stringify({ title, contentMd: contentMd?.slice(0, 2000) }),
  })
  return res.icon
}

// ─── Image attachment types & API ─────────────────────────────

export interface ImageAttachment {
  id: string
  filename: string
  mimeType: string
  width: number | null
  height: number | null
  sizeBytes: number
  caption: string | null
  entryId: string
  attachmentCaption: string | null
  attachedAt: string
  url: string
  thumbnailUrl: string | null
}

/** Get images attached to a notebook entry */
export async function getEntryImages(entryId: string): Promise<ImageAttachment[]> {
  return apiRequest<ImageAttachment[]>(`/images/notebook/${entryId}`)
}

/** Upload an image and attach it to a notebook entry */
export async function uploadAndAttachImage(
  file: File | Blob,
  entryId: string,
  caption?: string,
): Promise<ImageAttachment> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  // 1. Convert to JPEG client-side (handles HEIC from iOS)
  const jpegBlob = await convertToJpeg(file)
  const formData = new FormData()
  formData.append('file', jpegBlob, 'photo.jpg')
  if (caption) formData.append('caption', caption)

  const uploadRes = await fetch(`${API_BASE}/images/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: formData,
  })

  if (!uploadRes.ok) {
    const body = await uploadRes.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Upload failed: ${uploadRes.status}`)
  }

  const uploaded = await uploadRes.json() as { id: string }

  // 2. Attach to notebook entry
  invalidateCache(`/images/notebook/${entryId}`)
  return apiRequest<ImageAttachment>(`/images/${uploaded.id}/attach`, {
    method: 'POST',
    body: JSON.stringify({ notebookEntryId: entryId }),
  })
}

/** Detach (remove) an image from a notebook entry */
export async function detachImage(imageId: string, entryId: string): Promise<void> {
  invalidateCache(`/images/notebook/${entryId}`)
  await apiRequest<void>(`/images/${imageId}/detach/${entryId}`, {
    method: 'DELETE',
  })
}

// ─── Meeting follow-up suggestions ─────────────────────────────

export interface MeetingFollowupSuggestion {
  id: string
  notebookEntryId: string
  summary: string
  waitingOn: string | null
  expectedBy: string | null
  status: 'suggested' | 'approved' | 'dismissed'
  createdFollowupId: string | null
  createdAt: string
  updatedAt: string
}

export async function getFollowupSuggestions(
  entryId: string,
): Promise<MeetingFollowupSuggestion[]> {
  const res = await apiRequest<{ suggestions: MeetingFollowupSuggestion[] }>(
    `/notebook/${entryId}/followup-suggestions`,
  )
  return res.suggestions
}

export async function approveFollowupSuggestion(
  entryId: string,
  suggestionId: string,
): Promise<MeetingFollowupSuggestion> {
  invalidateCache(`/notebook/${entryId}/followup-suggestions`)
  const res = await apiRequest<{ suggestion: MeetingFollowupSuggestion }>(
    `/notebook/followup-suggestions/${suggestionId}/approve`,
    { method: 'POST' },
  )
  return res.suggestion
}

export async function dismissFollowupSuggestion(
  entryId: string,
  suggestionId: string,
): Promise<MeetingFollowupSuggestion> {
  invalidateCache(`/notebook/${entryId}/followup-suggestions`)
  const res = await apiRequest<{ suggestion: MeetingFollowupSuggestion }>(
    `/notebook/followup-suggestions/${suggestionId}/dismiss`,
    { method: 'POST' },
  )
  return res.suggestion
}

/** Batch update sortOrder for multiple notes (used during re-indexing) */
export async function batchUpdateSortOrder(
  updates: Array<{ id: string; sortOrder: number; parentId?: string | null }>,
): Promise<void> {
  await Promise.all(
    updates.map(({ id, sortOrder, parentId }) =>
      updateNotebookEntry(id, {
        sortOrder,
        ...(parentId !== undefined ? { parentId } : {}),
      })
    )
  )
}
